import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {useCallback, useMemo, useState} from 'react';
import Measure from 'react-measure';

import {getPlotFontSize} from '../../util/plotHelpers/plotFontSize';
import {useStablePanelId} from '../Panel/PanelIDContext';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {PanelGroupingContextProvider} from '../PanelRunsLinePlot/RunsLinePlotContext/usePanelGroupingSettings';
import {getRunSets} from '../PanelRunsLinePlot/utils/getRunSets';
import {PanelTitle} from '../PanelTitle/PanelTitle';
import {PCPlotSVG} from '../ParallelCoordinates/PCPlotSVG';
import {PCSettings} from '../ParallelCoordinates/PCSettings';
import {useLinePlotSettingsWithDefaults} from '../WorkspaceDrawer/Settings/runLinePlots/utils/getLinePlotSettings';
import {InstrumentedLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {getTitleFromConfig, ParallelCoordPanelProps} from './common';
import * as S from './PanelParallelCoord.styles';

// This component is the main entry point for the Parallel Coordinates panel
const ParallelCoordPanel: React.FC<ParallelCoordPanelProps> = React.memo(
  props => {
    const {config, updateConfig, pageQuery} = props;

    if (props.configMode) {
      return (
        <div className="chart-modal">
          <div className="chart-preview" style={{position: 'relative'}}>
            <PCPlotContainer {...props} />
          </div>
          <PCSettings
            config={config}
            updateConfig={updateConfig}
            pageQuery={pageQuery}
          />
        </div>
      );
    }

    return <PCPlotContainer {...props} />;
  }
);

export default ParallelCoordPanel;

// PCPlotContainer includes the plot metadata (title, error message, run count)
// The actual plot is rendered in the PCPlotSVG subcomponent, defined in PCPlotSVG.tsx
const PCPlotContainer: React.FC<ParallelCoordPanelProps> = React.memo(
  ({
    config,
    data,
    customRunColors,
    customRunNames,
    updateConfig,
    pageQuery,
  }) => {
    const {columns = []} = config;
    const {loading, totalRuns, limit: maxRuns, filtered: runs} = data;

    const [plotWidth, setPlotWidth] = useState(0);
    const [plotHeight, setPlotHeight] = useState(0);

    const errorMessage: JSX.Element | null = useMemo(() => {
      if (runs.length === 0) {
        return (
          <>
            Select runs to visualize data
            <br />
            in this parallel coordinates chart.
          </>
        );
      }
      if (columns.length === 0) {
        return (
          <>
            Select columns to visualize data
            <br />
            in this parallel coordinates chart.
          </>
        );
      }
      return null;
    }, [runs.length, columns.length]);

    const errorLogMessage: string | null = useMemo(() => {
      if (runs.length === 0) {
        return 'Select runs to visualize data in this parallel coordinates chart.';
      }
      if (columns.length === 0) {
        return 'Select columns to visualize data in this parallel coordinates chart.';
      }
      return null;
    }, [runs.length, columns.length]);

    const onResize = useCallback(({bounds}) => {
      setPlotHeight(bounds?.height ?? 0);
      setPlotWidth(bounds?.width ?? 0);
    }, []);

    const runSets = useMemo(() => getRunSets(pageQuery), [pageQuery]);

    const {useRunsTableGroupingInPanels} =
      useLinePlotSettingsWithDefaults(config);

    const stablePanelId = useStablePanelId();

    return (
      <PanelGroupingContextProvider
        aggregate={config.aggregate ?? false}
        aggregateMetrics={config.aggregateMetrics ?? false}
        groupAgg={config.groupAgg}
        groupArea={config.groupArea}
        groupBy={config.groupBy ?? ''}
        runSets={runSets}
        useRunsTableGroupingInPanels={useRunsTableGroupingInPanels}>
        <PanelTitle
          title={getTitleFromConfig(config)}
          fontSize={getPlotFontSize(config.fontSize ?? 'auto', plotHeight)}
        />

        <Measure bounds onResize={onResize}>
          {({measureRef}) => {
            return (
              <div
                className="chart-content"
                data-test="chart-content"
                ref={measureRef}>
                {loading && <Loader name="panel-parallel-loader" />}
                {!loading && errorMessage != null ? (
                  <PanelError
                    panelID={stablePanelId}
                    panelViewType="Parallel Coordinates Plot"
                    message={errorMessage}
                    logErrorMessage={
                      errorLogMessage || 'Parallel coordinates error occurred'
                    }
                  />
                ) : (
                  <>
                    <S.NumRuns>
                      {maxRuns && totalRuns > maxRuns && (
                        <S.NumRunsText>
                          Showing first {maxRuns} runs{' '}
                        </S.NumRunsText>
                      )}
                    </S.NumRuns>
                    <PCPlotSVG
                      config={config}
                      customRunColors={customRunColors}
                      customRunNames={customRunNames}
                      plotHeight={plotHeight}
                      plotWidth={plotWidth}
                      runs={runs}
                      updateConfig={updateConfig}
                    />
                  </>
                )}
              </div>
            );
          }}
        </Measure>
      </PanelGroupingContextProvider>
    );
  }
);
PCPlotContainer.displayName = 'PCPlotContainer';
